import React, { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import CheckmarkSvg from '../../../images/noun_checkmark.svg';
import CloseSvg from '../../../images/noun_close.svg';
import PlusSvg from '../../../images/noun_plus.svg';

import type {
  BackgroundColorProps,
  BackgroundProps,
  BorderProps,
  FlexboxProps,
  LayoutProps,
  PositionProps,
  SpaceProps,
} from 'styled-system';
import { border, color, flexbox, layout, position, space } from 'styled-system';
import Text from '../Text';
import useDropdown from '../../hooks/useDropdown';
import View from '../View';
import type { BoxProps } from '../Box/Box';
import Box from '../Box/Box';
import { useTheme } from 'styled-components';
import type { Theme } from '../../theme';
import { FloatingPortal } from '@floating-ui/react';
import { ArrowDownIcon } from '../../Icons';
import useEvent from '../../hooks/useEvent';
import { useHotkeys } from 'react-hotkeys-hook';

export interface StyledButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    SpaceProps,
    LayoutProps,
    FlexboxProps,
    BackgroundProps,
    BackgroundColorProps,
    BorderProps,
    PositionProps {
  height?: string | number;
  variant?: keyof Theme['button'];
  borderRadius?: string | number;
  hover?: React.CSSProperties;
}

// Get theme styles using style variant from props
const variantGet =
  (
    component: string,
    path: string,
    prop?: keyof Omit<StyledButtonProps, 'variant'>,
  ) =>
  ({ variant, ...restProps }: StyledButtonProps) =>
    (prop && restProps[prop]) ||
    themeGet(`${component}.${variant}.${path}`)(restProps);

const StyledButton = styled.button<StyledButtonProps>(
  (props) => ({
    alignItems: 'center',
    backgroundColor: variantGet(
      'button',
      'background',
      'backgroundColor',
    )(props),
    transition: 'background-color 0.2s ease-out',
    borderColor: variantGet('button', 'border', 'borderColor')(props),
    borderRadius: props.borderRadius ?? '30px',
    borderStyle: 'solid',
    borderWidth: '1px',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'inline-flex',
    height: props.height ?? '32px',
    justifyContent: 'center',
    minWidth: '0',
    padding: '4px 8px',
    opacity: props.disabled ? 0.2 : 1,
    '&:hover': {
      backgroundColor: variantGet(
        'button',
        'hover.background',
        'backgroundColor',
      )(props),
      ...props.hover,
    },
    '&:active': {
      backgroundColor: variantGet(
        'button',
        'active.background',
        'backgroundColor',
      )(props),
    },
  }),
  border,
  color,
  flexbox,
  layout,
  position,
  space,
);

const RoundButton = styled(StyledButton)`
  background-color: rgba(114, 132, 156, 0.8);
  border-radius: 12px;
  border-width: 0;
  height: 20px;
  width: 20px;
  padding: 0;
`;

const ButtonText = styled(Text)`
  font-family: 'Inter', 'Space Grotesk', 'Helvetica', fallback-font, sans-serif;
  font-weight: 600;
  border: none;
  color: ${variantGet('button', 'text', 'color')};
  user-select: none;
`;

export interface ButtonProps extends Omit<StyledButtonProps, 'onClick'> {
  children?: React.ReactNode;
  backgroundColor?: string;
  borderColor?: string;
  color?: string;
  onPress?: React.MouseEventHandler<HTMLButtonElement>;
  onPointerDown?: React.PointerEventHandler<HTMLButtonElement>;
}

const Button = forwardRef(
  (
    {
      children,
      variant = 'default',
      color,
      onPress,
      onPointerDown,
      ...restProps
    }: ButtonProps,
    ref: React.Ref<never>,
  ) => {
    const onClick = useEvent((e) => {
      e.preventDefault();
      e.stopPropagation();

      onPress?.(e);
    });

    return (
      <StyledButton
        ref={ref}
        variant={variant}
        onClick={onClick}
        onPointerDown={onPointerDown}
        {...restProps}
      >
        <ButtonText color={color} variant={variant} isUnselectable>
          {children}
        </ButtonText>
      </StyledButton>
    );
  },
);

export interface DropdownButtonProps extends BoxProps {
  children: React.ReactNode;
  closeOnContentClick?: boolean;
  label: React.ReactNode;
  variant?: keyof Theme['button'];
  disabled?: boolean;
  multiSelect?: boolean;
  borderRadius?: string | number;
  height?: string | number;
  onOpen?: () => void;
}

export const DropdownButton = ({
  children,
  closeOnContentClick = true,
  label,
  variant = 'default',
  disabled,
  multiSelect,
  borderRadius,
  height,
  onOpen,
  ...restProps
}: DropdownButtonProps): JSX.Element => {
  const theme = useTheme();

  const {
    isVisible,
    setMenuRef,
    setTargetRef,
    getMenuProps,
    setVisibility,
    styles,
  } = useDropdown({ closeOnContentClick });

  const [isMetaOrCtrlPressed, setIsMetaOrCtrlPressed] = useState(false);

  useHotkeys(
    'meta, ctrl',
    (event) => {
      if (!multiSelect) return;
      if (event.type === 'keydown') {
        setIsMetaOrCtrlPressed(true);
      } else {
        setIsMetaOrCtrlPressed(false);
        setVisibility(false);
      }
    },
    [multiSelect],
    { keydown: true, keyup: true, enabled: isVisible },
  );

  useEffect(() => {
    if (isMetaOrCtrlPressed) {
      setVisibility(true);
    }
    // isVisible is a necessary dependency, although it is not used in the effect
  }, [isMetaOrCtrlPressed, isVisible, setVisibility]);

  const isOpen = isVisible || isMetaOrCtrlPressed;

  useEffect(() => {
    if (isVisible && onOpen) {
      onOpen();
    }
  }, [isVisible, onOpen]);

  return (
    <>
      <Box ref={setTargetRef} {...restProps}>
        <Button
          onPress={() => setVisibility(!isVisible)}
          variant={variant}
          disabled={disabled}
          aria-label={typeof label === 'string' ? label : undefined}
          borderRadius={borderRadius}
          height={height}
        >
          <View display="flex" flexDirection="row" alignItems="center">
            <Text variant={variant} mr={1} isUnselectable>
              {label}
            </Text>
            <ArrowDownIcon fillColor={theme.button[variant].text} />
          </View>
        </Button>
      </Box>
      <FloatingPortal id="dropdown-root">
        <div ref={setMenuRef} style={styles}>
          {isOpen &&
            React.isValidElement(children) &&
            React.cloneElement(children, getMenuProps())}
        </div>
      </FloatingPortal>
    </>
  );
};

export const CheckButton = forwardRef<HTMLButtonElement | null, ButtonProps>(
  ({ onPress, ...restProps }: ButtonProps, ref): JSX.Element => {
    return (
      <RoundButton onClick={onPress} variant="dark" ref={ref} {...restProps}>
        <img src={CheckmarkSvg} width={10} height={10} />
      </RoundButton>
    );
  },
);

export const DeleteButton = forwardRef<HTMLButtonElement | null, ButtonProps>(
  ({ onPress, ...restProps }: ButtonProps, ref): JSX.Element => {
    return (
      <RoundButton onClick={onPress} variant="dark" ref={ref} {...restProps}>
        <img
          src={CloseSvg}
          width={10}
          height={10}
          style={{ position: 'relative' }}
        />
      </RoundButton>
    );
  },
);

export const PlusButton = forwardRef<HTMLButtonElement | null, ButtonProps>(
  ({ onPress, ...restProps }: ButtonProps, ref): JSX.Element => {
    return (
      <RoundButton onClick={onPress} variant="dark" ref={ref} {...restProps}>
        <img
          src={PlusSvg}
          width={10}
          height={10}
          style={{ position: 'relative' }}
        />
      </RoundButton>
    );
  },
);

export default Button;
